import { useState, useEffect, useRef } from "react";
import DocketDetailsDS from "../../DataServices/DocketDetailsDS";
import DatasetDS from "../../DataServices/DatasetDs";
import { useCustomContext } from "../CustomComponents/CustomComponents";
import { STATE_OPTIONS } from "../Datasets/StateOptions";
import { insertLog } from '../Logs/InsertLog';

const useAddDocketDetails = (onClose, refreshList, editData, existingDockets = []) => {

    const useremail = localStorage.getItem("email");
    const { showAlert, hud, stopHudRotation } = useCustomContext();

    const docketDS = new DocketDetailsDS();
    const datasetDS = new DatasetDS();

    // 🔹 STATES
    const [datasetList, setDatasetList] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    // const [isStateOpen, setIsStateOpen] = useState(false);

    const [job, setJob] = useState({
        dataset_id: "",
        docker_image_path: "",
        container_count: ""
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const originalDataRef = useRef(null);

    // 🔹 HANDLE INPUT CHANGE
    const handleChange = (e) => {
        const { name, value } = e.target;
        setJob(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 🔹 VALIDATION
    const validate = () => {
        let err = {};

        if (!job.dataset_id) {
            err.dataset_id = "DatasetID is missing. Please select the datasetID";
        }


        if (!job.docker_image_path) {
            err.docker_image_path = "Docker image path is required. Provide a valid image URL or name.";
        }

        if (!job.container_count || isNaN(job.container_count) || Number(job.container_count) <= 0) {
            err.container_count = "Enter a valid number greater than 0.";
        }

        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const isDuplicateDataset = () => {
        return existingDockets.some(d => {
            // allow same record in edit mode
            if (isEditMode && d._id === editingId) return false;

            return d.dataset_id === job.dataset_id;
        });
    };
    // 🔹 SAVE / UPDATE
    const handleSave = async () => {
        if (!validate()) return;
        const normalizedJob = { ...job };
        //   (edit mode only)
        if (isEditMode && originalDataRef.current) {
            const orig = originalDataRef.current;
            const noChanges =
                String(orig.dataset_id) === String(normalizedJob.dataset_id) &&
                String(orig.docker_image_path || "").trim() === String(normalizedJob.docker_image_path || "").trim() &&
                String(orig.container_count) === String(normalizedJob.container_count);

            if (noChanges) {
                showAlert("No changes detected.", [
                    { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
                ]);
                return;
            }
        }

        if (isDuplicateDataset()) {
            showAlert(`Dataset "${normalizedJob.dataset_id}" already has a docket entry`, [
                { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
            ]);
            return;
        }

        const payload = {
            ...normalizedJob,
            container_count: Number(normalizedJob.container_count)
        };

        try {
            setLoading(true);
            hud("Saving...");

            let response;

            if (isEditMode) {
                response = await docketDS.WS_Data_Scrapper_Docker_Details_UPDATE({
                    _id: editingId,
                    ...payload,
                    updated_by_email_id: useremail
                });
            } else {
                response = await docketDS.WS_Data_Scrapper_Docker_Details_INSERT({
                    ...payload,
                    created_by_email_id: useremail
                });
            }

            const data = response.data;
            const body =
                typeof data.body === "string"
                    ? JSON.parse(data.body)
                    : data.body;

            if (data.statusCode === 200) {
                insertLog(
                    `${useremail?.split('@')[0]} ${isEditMode ? "updated" : "added"} docket for dataset ${job.dataset_id}`,
                    0,
                    useremail,
                    hud,
                    stopHudRotation,
                    showAlert,
                    editingId || '',
                    isEditMode ? 'UPDATE' : 'INSERT',
                    isEditMode ? 'WS_Data_Scrapper_Docker_Details_UPDATE' : 'WS_Data_Scrapper_Docker_Details_INSERT',
                    'SUCCESS',
                    ''
                );
                showAlert(
                    isEditMode ? "Docket updated successfully" : "Docket added successfully",
                    [{ label: "OK", onClick: () => { }, color: "var(--buttonColor)" }]
                );
                refreshList?.();
                onClose();
            } else {
                insertLog(
                    `${useremail?.split('@')[0]} failed to ${isEditMode ? "update" : "add"} docket for dataset ${job.dataset_id}`,
                    0,
                    useremail,
                    hud,
                    stopHudRotation,
                    showAlert,
                    editingId || '',
                    isEditMode ? 'UPDATE' : 'INSERT',
                    isEditMode ? 'WS_Data_Scrapper_Docker_Details_UPDATE' : 'WS_Data_Scrapper_Docker_Details_INSERT',
                    'FAILED',
                    'Operation failed'
                );
                showAlert(body?.message || "Operation failed", [
                    {
                        label: "OK",
                        onClick: () => { },
                        color: "var(--buttonColor)"
                    }
                ]);
            }

        } catch (err) {
            insertLog(
                `${useremail?.split('@')[0]} failed to ${isEditMode ? "update" : "add"} docket for dataset ${job.dataset_id}`,
                0,
                useremail,
                hud,
                stopHudRotation,
                showAlert,
                editingId || '',
                isEditMode ? 'UPDATE' : 'INSERT',
                isEditMode ? 'WS_Data_Scrapper_Docker_Details_UPDATE' : 'WS_Data_Scrapper_Docker_Details_INSERT',
                'FAILED',
                err.message
            );
            showAlert(err.message || "Error while saving docket", [
                { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
            ]);
        } finally {
            setLoading(false);
            stopHudRotation();
        }
    };

    // 🔹 FETCH DATASETS
    useEffect(() => {
        if (editData) return;
        const fetchDatasets = async () => {
            try {
                const response = await datasetDS.AP_Dataset_List_GET({});

                const data = response.data;
                const body =
                    typeof data.body === "string"
                        ? JSON.parse(data.body)
                        : data.body;

                setDatasetList(body.records || []);
            } catch (err) {
                insertLog(
                    `${useremail?.split('@')[0]} failed to fetch datasets`,
                    0,
                    useremail,
                    hud,
                    stopHudRotation,
                    showAlert,
                    '',
                    'GET',
                    'AP_Dataset_List_GET',
                    'FAILED',
                    err.message
                );
                console.error("Failed to fetch datasets:", err);
            }
        };

        fetchDatasets();
    }, []);

    // 🔹 PREFILL FOR EDIT MODE
    useEffect(() => {
        if (editData) {
            const prefill = {
                dataset_id: editData.dataset_id || "",
                docker_image_path: editData.docker_image_path || "",
                container_count: editData.container_count || ""
            };
            setJob(prefill);

            originalDataRef.current = { ...prefill };

            setEditingId(editData._id);
            setIsEditMode(true);
        }
    }, [editData]);

    // 🔹 CLOSE DROPDOWN ON OUTSIDE CLICK
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest(".custom-select")) {
                setIsOpen(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);


    // 🔹 RETURN
    return {
        datasetList,
        isOpen,
        setIsOpen,
        job,
        setJob,
        errors,
        loading,
        handleChange,
        handleSave,
        isEditMode
    };
};

export default useAddDocketDetails;